import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { get } from "@/lib/api";
import { PageHeader, Section } from "@/components/common/PageHeader";
import { KpiCard } from "@/components/common/KpiCard";
import { Skeleton } from "@/components/common/EmptyState";
import { money, dateFmt } from "@/lib/format";
import { Users, TrendingDown, ClipboardCheck, Wallet, Download } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, CartesianGrid, LineChart, Line } from "recharts";

const CHART = ["#8B5CF6", "#2563EB", "#EF4444", "#F59E0B", "#22C55E", "#0EA5E9"];

function exportCsv(filename, rows) {
  const csv = rows.map((r) => r.map((c) => `"${c}"`).join(",")).join("\n");
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
  a.download = filename;
  a.click();
}

export default function HRDashboard() {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({ queryKey: ["hrms-analytics"], queryFn: () => get("/hrms/analytics") });

  if (isLoading || !data) {
    return <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-28" />)}</div>;
  }

  return (
    <div>
      <PageHeader title="HR Analytics" subtitle="Headcount, attrition, review completion and payroll cost at a glance." />

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <KpiCard testid="hr-kpi-headcount" variant="hero" accent="blue" label="Headcount" value={data.headcount_total} sub="active employees" icon={Users} />
        <KpiCard testid="hr-kpi-attrition" accent="red" label="Attrition Rate" value={`${data.attrition.rate_pct}%`} sub={`${data.attrition.exits_last_12mo} exits (12mo)`} icon={TrendingDown} />
        <KpiCard testid="hr-kpi-review" accent="amber" label="Review Completion" value={data.review_completion ? `${data.review_completion.pct_submitted}%` : "—"} sub={data.review_completion?.cycle_name || "no active cycle"} icon={ClipboardCheck} />
        <KpiCard testid="hr-kpi-payroll" accent="primary" label="Latest Payroll Cost" value={data.payroll_cost.length ? money(data.payroll_cost[data.payroll_cost.length - 1].total_net) : "—"} sub={data.payroll_cost.length ? data.payroll_cost[data.payroll_cost.length - 1].month : ""} icon={Wallet} />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Section title="Headcount by Department" className="lg:col-span-2" action={
          <button data-testid="export-headcount" onClick={() => exportCsv("headcount.csv", [["Department", "Count"], ...data.headcount_by_dept.map((d) => [d.department, d.count])])}
            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"><Download className="h-3.5 w-3.5" />Export</button>
        }>
          <div className="p-4" style={{ height: 240 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.headcount_by_dept}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="department" tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
                <Bar dataKey="count" fill="#2563EB" radius={[6, 6, 0, 0]} maxBarSize={48} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Section>

        <Section title="Headcount Mix">
          <div className="p-4" style={{ height: 240 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={data.headcount_by_dept} dataKey="count" nameKey="department" cx="50%" cy="50%" innerRadius={45} outerRadius={80} paddingAngle={2}>
                  {data.headcount_by_dept.map((_, i) => <Cell key={i} fill={CHART[i % CHART.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Section>

        <Section title="Payroll Cost Trend" className="lg:col-span-2" action={
          <button data-testid="export-payroll-cost" onClick={() => exportCsv("payroll-cost.csv", [["Month", "Gross", "Net"], ...data.payroll_cost.map((p) => [p.month, p.total_gross, p.total_net])])}
            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"><Download className="h-3.5 w-3.5" />Export</button>
        }>
          <div className="p-4" style={{ height: 240 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.payroll_cost}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
                <Line type="monotone" dataKey="total_net" stroke="#22C55E" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="total_gross" stroke="#94A3B8" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Section>

        <Section title="Recent Hires & Exits">
          <div className="divide-y divide-border">
            {data.recent_hires.map((h) => (
              <button key={h.id} onClick={() => navigate(`/hrms/directory/${h.id}`)} className="flex w-full items-center justify-between px-4 py-2.5 text-left text-sm hover:bg-muted/50">
                <span>{h.name}</span><span className="text-xs text-emerald-600">Joined {dateFmt(h.joining_date)}</span>
              </button>
            ))}
            {data.recent_exits.map((e) => (
              <button key={e.id} onClick={() => navigate(`/hrms/directory/${e.id}`)} className="flex w-full items-center justify-between px-4 py-2.5 text-left text-sm hover:bg-muted/50">
                <span>{e.name}</span><span className="text-xs text-red-500">Exited {dateFmt(e.exit_date)}</span>
              </button>
            ))}
            {!data.recent_hires.length && !data.recent_exits.length && <div className="p-6 text-center text-sm text-muted-foreground">No recent activity.</div>}
          </div>
        </Section>
      </div>
    </div>
  );
}
